import {
  CSSProperties,
  FC,
  forwardRef,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';

import {
  Icon,
  Typography,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { Desk, Rect } from 'src/types';

import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

import classes from './DeskPlace.module.scss';

const chairRatio = 0.55;
const borderRadiusCoef = 0.15;
const iconCoef = 0.8;
const chairOffset = 1;

export interface DeskPlaceLayoutProps {
  item: Desk;
  figureClassName?: string;
  deskClassName?: string;
  chairClassName?: string;
  titleClassName?: string;
  iconName?: Icons;
  disabled?: boolean;
  onMouseOver?: (item: Desk) => void;
  onMouseLeave?: (item: Desk) => void;
}

export const DeskPlaceLayout = forwardRef<HTMLDivElement, DeskPlaceLayoutProps>(
  function Inner(
    {
      item,
      figureClassName,
      deskClassName,
      chairClassName,
      titleClassName,
      iconName,
      disabled,
      onMouseOver,
      onMouseLeave,
    },
    ref,
  ) {
    const { width, height, fontSize, rotation } = item;

    const x = item.x - width / 2;
    const y = item.y - height / 2;

    const borderRadius = borderRadiusCoef * width;
    const chairHeight = height * chairRatio;
    const chairTop = (height - chairHeight) / 2;
    const containerStyle = useMemo(
      (): CSSProperties => ({
        transform: `translate(${x}px,${y}px)`,
        width: `${width}px`,
        height: `${height}px`,
      }),
      [height, width, x, y],
    );
    const deskStyle = useMemo(
      (): CSSProperties => ({
        borderRadius: `${borderRadius}px`,
        transform: `rotate(${rotation}deg)`,
      }),
      [borderRadius, rotation],
    );
    const chairStyle = useMemo(
      (): CSSProperties => ({
        height: `${chairHeight}px`,
        width: `${chairHeight / 2}px`,
        top: `${chairTop}px`,
        right: `${-(chairHeight / 2 + chairOffset)}px`,
        borderRadius: `0 ${height}px ${height}px 0`,
      }),
      [chairHeight, chairTop, height],
    );
    const titleStyle = useMemo(
      (): CSSProperties => ({
        fontSize: `${fontSize}px`,
      }),
      [fontSize],
    );
    const iconSize = `${iconCoef * width}px`;
    const iconStyle = useMemo(
      (): CSSProperties => ({
        fontSize: iconSize,
        width: iconSize,
        height: iconSize,
        lineHeight: iconSize,
      }),
      [iconSize],
    );
    const delimiterStyle = useMemo(
      (): CSSProperties => ({
        width: `${chairOffset}px`,
        height: `${chairHeight}px`,
        top: `${chairTop}px`,
        right: `${-chairOffset}px`,
      }),
      [chairHeight, chairTop],
    );
    const id = item.mapPlaceId;
    const placeId = disabled ? undefined : id;

    const handleMouseOver = useCallback(() => {
      onMouseOver?.(item);
    }, [item, onMouseOver]);

    const handleMouseLeave = useCallback(() => {
      onMouseLeave?.(item);
    }, [item, onMouseLeave]);

    return (
      <div
        className={clsx(
          classes.container,
          disabled ? classes.disabledContainer : classes.enabledContainer,
        )}
        style={containerStyle}
        data-testid={disabled ? undefined : `place-${id}`}
        data-title={item.title}
        ref={ref}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={clsx(
            classes.figure,
            disabled ? classes.disabled : classes.enabled,
            figureClassName,
          )}
        >
          <div
            className={clsx(
              classes.figure,
              disabled ? classes.disabled : classes.enabled,
              deskClassName,
            )}
            style={deskStyle}
          >
            <div className={clsx(classes.desk)} data-place-id={placeId} />
            <div
              className={clsx(
                classes.delimiter,
                disabled ? classes.disabled : classes.enabled,
              )}
              style={delimiterStyle}
            />
            <div
              className={clsx(
                classes.chair,
                disabled ? classes.disabled : classes.enabled,
                chairClassName,
              )}
              style={chairStyle}
              data-place-id={placeId}
            />
          </div>
          <div data-place-id={placeId} className={classes.content}>
            {iconName ? (
              <Icon
                className={clsx(classes.text, titleClassName)}
                style={iconStyle}
                iconName={iconName}
              />
            ) : (
              <Typography
                className={clsx(classes.text, titleClassName)}
                variant='body3'
                style={titleStyle}
              >
                {item.title}
              </Typography>
            )}
          </div>
        </div>
      </div>
    );
  },
);

const chairByRotation = {
  left: 180,
  right: 0,
  top: 270,
  bottom: 90,
};

export const deskToRect = (desk: Desk): Rect => {
  const chairSize = (chairRatio * desk.height) / 2 + chairOffset;
  let halfWidth = desk.width / 2;
  let halfHeight = desk.height / 2;
  if (desk.rotation % 180) {
    halfWidth = desk.height / 2;
    halfHeight = desk.width / 2;
  }

  return {
    type: 'rect',
    left:
      desk.x -
      halfWidth -
      +(desk.rotation === chairByRotation.left) * chairSize,
    right:
      desk.x +
      halfWidth +
      +(desk.rotation === chairByRotation.right) * chairSize,
    top:
      desk.y -
      halfHeight -
      +(desk.rotation === chairByRotation.top) * chairSize,
    bottom:
      desk.y +
      halfHeight +
      +(desk.rotation === chairByRotation.bottom) * chairSize,
  };
};

export interface DeskPlaceProps extends DeskPlaceLayoutProps {
  title?: ReactNode;
}

export const DeskPlace: FC<DeskPlaceProps> = ({ title, ...rest }) => {
  return title ? (
    <TooltipWrapper title={title}>
      <DeskPlaceLayout {...rest} />
    </TooltipWrapper>
  ) : (
    <DeskPlaceLayout {...rest} />
  );
};
